/**
 * LES TRACES DES AUTRES (§Q21, §4.4) — sous les choix, en encre pâle, et jamais plus de trois.
 *
 * C'est le seul lien entre deux joueurs de Destinia, et il ne ressemble à rien de ce qu'un jeu en
 * ligne fait d'habitude : **personne n'est là**. On ne voit pas d'avatar, on ne reçoit pas de
 * message, on ne rejoint personne. On lit ce que les précédents ont laissé, comme on lit une croix
 * gravée sur un arbre.
 *
 * TROIS LIGNES AU PLUS, et c'est une règle de design (§3), pas une commodité : une aventure très
 * fréquentée ne doit pas enterrer son propre texte sous le passage des autres. Le texte reste le
 * sujet — c'est un livre-jeu.
 *
 * LE TAUX DE SURVIE EST UNE MENTION, JAMAIS UNE JAUGE. Une barre rouge sur un lieu transforme une
 * information de pairs en verdict du jeu, et le joueur cesse d'explorer pour suivre la couleur. Le
 * serveur se tait déjà sous cinq passages ; ici on n'ajoute pas de couleur à ce qu'il consent à
 * dire.
 */
import type { LesTraces, TraceEnVue } from "../api/types.js";
import { Glyphe } from "./Glyphe.js";
import { t, tn } from "../lexique/langue.js";

/** Combien on en montre. Les plus récentes : ce qui vient d'arriver ici en dit plus long sur l'état
 * du lieu que ce qui s'y est passé il y a trois mois. */
const AU_PLUS = 3;

/**
 * CE QU'UNE TRACE RACONTE, en une phrase, à la troisième personne.
 *
 * Elle dit ce qu'un joueur a FAIT — il est tombé, il a vidé, il a abattu — jamais ce que le monde
 * EST. C'est ce qui la distingue d'un guide, et c'est une frontière que le serveur tient déjà : ni
 * le contenu de la cache, ni les caractéristiques du boss, ni les dégâts qui ont tué ne sortent de
 * l'API. Ici on n'a donc rien à retirer — seulement à bien dire.
 *
 * TOUS LES VERBES SONT INVARIABLES, ET C'EST DÉLIBÉRÉ. « Berthe est tombée » et « Aubry est tombé »
 * demanderaient de connaître le genre d'un héros — un nom ne le dit pas, la création ne le demande
 * pas, et le §3.1 n'en veut pas. « A péri », « a fouillé », « a abattu » s'accordent avec rien :
 * c'est du français juste pour tout le monde, plutôt qu'un accord juste pour la moitié.
 */
export function phraseDeTrace(trace: TraceEnVue): string {
  const heros = trace.heros;
  if (trace.espece === "death") {
    return trace.cause
      ? t("{heros} a péri ici — {cause}.", { heros, cause: trace.cause })
      : t("{heros} a péri ici.", { heros });
  }
  if (trace.espece === "looted") return t("{heros} a fouillé les lieux avant vous.", { heros });
  if (trace.espece === "boss_scar") {
    return trace.tours
      ? tn(
          trace.tours,
          "{heros} a déjà abattu ce qui garde ce lieu, en {n} tour.",
          "{heros} a déjà abattu ce qui garde ce lieu, en {n} tours.",
          { heros }
        )
      : t("{heros} a déjà abattu ce qui garde ce lieu.", { heros });
  }
  return t("{heros} a laissé un mot ici.", { heros });
}

const GLYPHES: Record<string, "crane" | "sac" | "epee" | "plume"> = {
  death: "crane",
  looted: "sac",
  boss_scar: "epee",
  note: "plume"
};

export function Traces({ traces }: { traces: LesTraces | null }) {
  if (!traces) return null;
  const lignes = traces.traces.slice(0, AU_PLUS);
  if (!lignes.length && !traces.survie) return null;

  return (
    <aside className="traces" aria-label={t("Ce que d'autres ont laissé ici")}>
      {lignes.map((trace) => (
        <p className="traces__ligne" key={`${trace.espece}-${trace.heros}-${trace.le}`}>
          <Glyphe nom={GLYPHES[trace.espece] ?? "plume"} taille={13} />
          <span>{phraseDeTrace(trace)}</span>
        </p>
      ))}
      {traces.survie ? (
        <p className="traces__ligne traces__survie">
          {/* LE CHIFFRE SE DIT, IL NE SE DESSINE PAS. Et il dit sur quoi il porte : « 60 % » tout
              seul se lit comme une promesse du jeu ; « sur 15 passages » se lit comme ce que c'est,
              le témoignage de quinze inconnus. */}
          <span>
            {t("{pourcent} % en sont repartis vivants,", { pourcent: traces.survie.pourcent })}{" "}
            <span className="faible">{tn(traces.survie.passages, "sur {n} passage.", "sur {n} passages.")}</span>
          </span>
        </p>
      ) : null}
    </aside>
  );
}
